import React from 'react';
import { Container, Box, Typography, Chip, Divider, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
  color: 'white',
  padding: '64px 0',
  textAlign: 'center',
});

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>{children}</Typography>
);

function CodeOfConductPage() {
  return (
    <Box>
      <SEO title="Intern Code of Conduct" description="Standards every CWC intern agrees to uphold." />
      <HeroSection>
        <Container maxWidth="lg">
          <Chip label="For Interns" sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Intern Code of Conduct
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            The standards every CWC intern agrees to uphold
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 3, mb: 4, backgroundColor: 'rgba(124,58,237,0.05)', borderRadius: 3, border: '1px solid rgba(124,58,237,0.2)' }}>
          <Typography variant="body1" sx={{ lineHeight: 1.8, fontStyle: 'italic' }}>
            As a CWC intern, you represent Cyber Wise Corp, your college, and the cybersecurity profession every time you walk into a client's home or business. This Code of Conduct sets the expectations for your behavior, professionalism, and ethics. Violating this code may result in immediate suspension or removal from the CWC program.
          </Typography>
        </Paper>

        <Divider sx={{ my: 4 }} />

        <Section title="1. Professionalism">
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li>Arrive on time for every assessment. If you will be late or need to cancel, notify the client and CWC as far in advance as possible.</li>
            <li>Dress professionally. Business casual is the minimum standard. No offensive graphics, slogans, or clothing with visible branding from other companies.</li>
            <li>Introduce yourself by name, show your CWC identification, and explain the assessment process before beginning.</li>
            <li>Communicate clearly and respectfully with clients at all times. Many clients are not technical. Explain findings in plain language without condescension.</li>
            <li>Complete every assessment thoroughly using the full CWC assessment framework. Do not rush or skip sections.</li>
          </Box>
        </Section>

        <Section title="2. Privacy and Confidentiality">
          <P>This is the most critical section of this Code of Conduct. Privacy violations are grounds for immediate removal.</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li><strong>Never access, open, view, copy, photograph, or record any personal files, emails, photos, documents, financial records, browsing history, or private content on any client device.</strong></li>
            <li>Never record or retain WiFi passwords, login credentials, or any authentication information beyond what is needed during the active assessment.</li>
            <li>Purge all connection data from your assessment device after every visit. CWC will audit devices periodically.</li>
            <li>Never share client information (name, address, phone number, assessment findings) with anyone outside of the CWC platform. This includes social media, personal conversations, and other interns.</li>
            <li>Never photograph the interior of a client's home or business unless specifically required for the assessment report and approved by the client.</li>
            <li>If you accidentally encounter sensitive personal information during the assessment (for example, a document left open on a screen), look away, do not read it, and do not mention it.</li>
          </Box>
        </Section>

        <Section title="3. Safety and Boundaries">
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li>Only enter rooms or areas the client has explicitly granted you access to. Ask before entering any room.</li>
            <li>Do not move, rearrange, or unplug any equipment without asking the client first.</li>
            <li>If you feel unsafe at any time during an assessment, you have the right to end the assessment immediately. Contact CWC and we will handle the situation.</li>
            <li>Never be alone with a minor. If the only adult present needs to leave during the assessment, pause the assessment until they return.</li>
            <li>Do not accept food, drinks, gifts, or tips from clients. A polite "no thank you" is appropriate.</li>
          </Box>
        </Section>

        <Section title="4. Technical Integrity">
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li>Follow the CWC assessment framework exactly as trained. Do not invent your own scoring criteria or skip required checks.</li>
            <li>Report findings accurately. Never inflate or minimize scores to make a report look better or worse than reality.</li>
            <li>If you are unsure about a finding, mark it for expert review rather than guessing. The reviewing expert would rather see an honest "I'm not sure" than an incorrect assessment.</li>
            <li>Do not install, remove, or modify any software on a client's device unless it is part of the approved assessment procedure and the client has given explicit consent.</li>
            <li>Do not change any settings on a client's devices or network. The assessment is observational. Recommendations go in the report.</li>
            <li>Never use a client's network connection for personal use (browsing, email, downloads, streaming).</li>
          </Box>
        </Section>

        <Section title="5. Communication and Reporting">
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li>Submit your assessment report within 4 hours of completing the on-site visit. Do not leave reports for the next day.</li>
            <li>If you discover a critical vulnerability during the assessment (exposed network, active malware, compromised device), alert the client immediately during the consultation and flag it as urgent in your report.</li>
            <li>During the 15-minute consultation, walk the client through your initial findings clearly. Answer their questions patiently.</li>
            <li>Respond to CWC communications (messages, scheduling notifications, feedback from expert reviewers) within 24 hours.</li>
          </Box>
        </Section>

        <Section title="6. Representation and Social Media">
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li>You may identify yourself as a CWC intern on your resume, LinkedIn, and professional profiles.</li>
            <li>Do not post about specific clients, assessment details, addresses, or findings on any social media platform.</li>
            <li>Do not make public statements on behalf of CWC without prior written approval.</li>
            <li>If a media outlet or journalist contacts you about CWC, direct them to our contact form and do not provide comments.</li>
          </Box>
        </Section>

        <Section title="7. Conflicts of Interest">
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li>Do not solicit clients for outside work, consulting, or services during or after an assessment.</li>
            <li>Do not recommend specific products, services, or vendors to clients in exchange for compensation or referral fees.</li>
            <li>If a client asks you to perform work outside the CWC assessment scope (personal IT support, software installation, etc.), politely decline and direct them to contact CWC if they need additional services.</li>
            <li>Disclose any personal relationship with a client before accepting an assessment booking.</li>
          </Box>
        </Section>

        <Section title="8. Consequences of Violations">
          <P>Violations of this Code of Conduct are taken seriously and may result in:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.7 } }}>
            <li><strong>Written warning</strong> for minor first-time infractions (tardiness, dress code, incomplete reports)</li>
            <li><strong>Suspension</strong> from accepting new bookings pending investigation for moderate infractions</li>
            <li><strong>Immediate removal</strong> from the CWC program for serious violations including privacy breaches, dishonesty in reporting, harassment, or any illegal activity</li>
          </Box>
          <P>
            Interns who are removed from the program for Code of Conduct violations will not be eligible to rejoin. CWC reserves the right to report serious violations to the intern's college program.
          </P>
        </Section>

        <Section title="9. Acknowledgment">
          <P>
            By registering as a CWC intern and accepting your first assessment booking, you acknowledge that you have read, understand, and agree to abide by this Code of Conduct for the duration of your engagement with Cyber Wise Corp.
          </P>
        </Section>
      </Container>
    </Box>
  );
}

export default CodeOfConductPage;
